import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useOutletContext, useParams, Navigate, Link } from 'react-router-dom';
import { Plus, Copy, Check } from 'lucide-react';
import type { OutletCtx } from '@/layouts/AdminLayout';
import { api, CategorySummary, Item } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { useToast } from '@/components/ui/Toast';
import { AddCategoryDialog } from '@/components/AddCategoryDialog';
import { ItemEditDialog } from '@/components/ItemEditDialog';
import { ItemsTable } from '@/components/ItemsTable';
import { ChangeLogPanel } from '@/components/ChangeLogPanel';
import { cn } from '@/lib/utils';

export default function CategoryPage() {
  const { siteSlug, categorySlug } = useParams();
  const { sites, refreshSites } = useOutletContext<OutletCtx>();
  const navigate = useNavigate();
  const { push } = useToast();

  const site = useMemo(() => sites.find((s) => s.slug === siteSlug), [sites, siteSlug]);
  const [categories, setCategories] = useState<CategorySummary[]>([]);
  const [reloadKey, setReloadKey] = useState(0);
  const [editMode, setEditMode] = useState<null | { kind: 'create'; categoryId: number } | { kind: 'edit'; item: Item }>(
    null
  );
  const [addCatOpen, setAddCatOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const currentCat = useMemo(
    () => categories.find((c) => c.slug === categorySlug),
    [categories, categorySlug]
  );

  const refreshCategories = useCallback(async () => {
    if (!site) return;
    try {
      const list = await api.listCategories(site.id);
      setCategories(list);
    } catch (e) {
      push(e instanceof Error ? e.message : '카테고리 로드 실패', 'error');
    }
  }, [site, push]);

  useEffect(() => {
    void refreshCategories();
  }, [refreshCategories]);

  const onChanged = useCallback(() => {
    setReloadKey((k) => k + 1);
    void refreshSites();
    void refreshCategories();
  }, [refreshSites, refreshCategories]);

  if (!site) {
    if (sites.length === 0) return <div className="text-sm text-muted-foreground">로드 중…</div>;
    return <Navigate to="/admin" replace />;
  }
  if (!currentCat && categories.length > 0) {
    return <Navigate to={`/admin/sites/${site.slug}/${categories[0].slug}`} replace />;
  }
  if (!currentCat) return <div className="text-sm text-muted-foreground">카테고리 로드 중…</div>;

  const publicHtmlUrl = `${window.location.origin}/api/sites/${site.slug}/categories/${currentCat.slug}/html`;
  const publicJsonUrl = `${window.location.origin}/api/sites/${site.slug}/categories/${currentCat.slug}/items`;

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">{site.name}</h1>
          <div className="text-xs text-muted-foreground">/{site.slug}</div>
        </div>
        <Button onClick={() => setEditMode({ kind: 'create', categoryId: currentCat.id })}>
          <Plus className="h-4 w-4" /> 새 아이템
        </Button>
      </div>

      <div className="flex flex-wrap gap-2">
        {categories.map((c) => (
          <Link
            key={c.id}
            to={`/admin/sites/${site.slug}/${c.slug}`}
            className={cn(
              'rounded-md border px-3 py-1.5 text-sm transition-colors',
              c.id === currentCat.id
                ? 'border-primary bg-primary text-primary-foreground'
                : 'bg-card hover:bg-accent'
            )}
          >
            {c.name}{' '}
            <span
              className={cn(
                'ml-1 rounded-full px-1.5 py-0.5 text-[10px]',
                c.id === currentCat.id ? 'bg-primary-foreground/20' : 'bg-muted text-muted-foreground'
              )}
            >
              {c.active_item_count}
            </span>
          </Link>
        ))}
        <Button variant="outline" size="sm" onClick={() => setAddCatOpen(true)}>
          <Plus className="h-4 w-4" /> 카테고리
        </Button>
      </div>

      <div className="rounded-md border bg-muted/30 px-4 py-3 text-xs">
        <div className="mb-2 font-semibold">공개 엔드포인트 (무인증)</div>
        <div className="flex items-center gap-2">
          <code className="flex-1 truncate rounded bg-background px-2 py-1">{publicHtmlUrl}</code>
          <button
            onClick={() => {
              navigator.clipboard.writeText(publicHtmlUrl);
              setCopied(true);
              setTimeout(() => setCopied(false), 1500);
            }}
            className="rounded p-1 hover:bg-accent"
            aria-label="copy"
          >
            {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
          </button>
        </div>
        <div className="mt-1 flex items-center gap-2">
          <code className="flex-1 truncate rounded bg-background px-2 py-1">{publicJsonUrl}</code>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        <ItemsTable
          categoryId={currentCat.id}
          reloadKey={reloadKey}
          onEdit={(it) => setEditMode({ kind: 'edit', item: it })}
          onChanged={onChanged}
        />
        <ChangeLogPanel reloadKey={reloadKey} />
      </div>

      <ItemEditDialog
        open={!!editMode}
        mode={editMode}
        onOpenChange={(v) => {
          if (!v) setEditMode(null);
        }}
        onSaved={onChanged}
      />
      <AddCategoryDialog
        open={addCatOpen}
        onOpenChange={setAddCatOpen}
        siteId={site.id}
        onCreated={() => {
          void refreshCategories();
          void refreshSites();
          navigate(`/admin/sites/${site.slug}`, { replace: true });
        }}
      />
    </div>
  );
}
